// app/composables/season/matches.ts

import type { TDateISO } from "~/src/Shared/DateTypes";
import type { DateTimeFormatOptions } from "@intlify/core-base";
import type { UseFetchParams } from "~/src/Shared/Types";
import type { CountryTimeZone } from "~/src/match/domain/models/Match";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import CountryTimeZoneData from "~/src/match/datasources/TimezoneCountryEquivalence.json";

interface UseMatches {
  useFetchParamsMatchData: (matchId: number, live: boolean) => UseFetchParams;
  formatMatchTime: (
    locale: string,
    matchDate: TDateISO,
    fallbackTimeZone: string,
    info: "stadium" | "country" | "appendix",
    infoId: number | string
  ) => string;
  setMatchTimeZone: (
    info: "stadium" | "country" | "appendix",
    infoId: number | string
  ) => CountryTimeZone | undefined;
  showTime: (time: string) => boolean;
  showDate: (time: string) => boolean;
}

export const useMatches = (): UseMatches => {
  return {
    useFetchParamsMatchData,
    formatMatchTime,
    setMatchTimeZone,
    showTime,
    showDate,
  };

  function useFetchParamsMatchData(matchId: number, live: boolean): UseFetchParams {
    const { region } = useCompetitionStore();

    const path = `/api/v1/competition/matches/${matchId}`;
    const opts = {
      query: { live, competitionId: region?.competitionId },
    };

    return { path, opts };
  }

  function setMatchTimeZone(
    info: "stadium" | "country" | "appendix",
    infoId: number | string
  ): CountryTimeZone | undefined {
    const timeZoneCountriesData: CountryTimeZone[] = CountryTimeZoneData;

    if (info === "stadium" && typeof infoId === "number") {
      return timeZoneCountriesData.find(c => c.stadiums?.includes(infoId));
    } else if (info === "country" && typeof infoId === "number") {
      return timeZoneCountriesData.find(c => c.id === infoId);
    } else if (info === "appendix" && typeof infoId === "string") {
      const appendix = infoId.toUpperCase();
      return timeZoneCountriesData.find(c => c.appendix === appendix);
    } else {
      return undefined;
    }
  }

  function formatMatchTime(
    locale: string,
    matchDate: TDateISO,
    fallbackTimeZone: string,
    info: "stadium" | "country" | "appendix",
    infoId: number | string
  ): string {
    const timeZoneInfo = setMatchTimeZone(info, infoId);

    const dateOptions: DateTimeFormatOptions = {
      timeStyle: "short",
      timeZone: timeZoneInfo?.timezone || fallbackTimeZone,
    };

    return new Date(matchDate).toLocaleString(locale, dateOptions) + " " + (timeZoneInfo?.appendix || "");
  }

  function showDate(time: string): boolean {
    return !time.includes(":02");
  }

  function showTime(time: string): boolean {
    return !time.includes(":01") && !time.includes(":02");
  }
};
